import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { alpha, Box, Button, Card, CircularProgress, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { api } from "../../api/axios";
import { IconTile, Pill } from "../../components/ui";
import { CheckList, DirArrow, PageHero, Section } from "./MarketingParts";

function normalizeFeatures(features) {
  if (!features) return {};
  if (typeof features === "object") return features;
  try {
    return JSON.parse(features);
  } catch {
    return {};
  }
}

const KNOWN_FEATURES = [
  "font",
  "contrast",
  "links",
  "stopAnimations",
  "invertColors",
  "readableFont",
  "readLine",
  "lineHeight",
  "letterSpacing",
  "statement",
  "aiAudit",
  "aiAltText",
  "aiColorSuggestions",
  "whiteLabel",
  "branding",
];

export default function MarketingPricing() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/api/plans")
      .then(({ data }) => {
        setPlans(data.plans || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const featureLabel = (key) => (KNOWN_FEATURES.includes(key) ? t(`mk_feat_${key}`) : key);
  const locale = i18n.language === "en" ? "en-US" : "he-IL";

  const intervals = useMemo(() => {
    const set = new Set(plans.map((p) => (p.interval === "yearly" ? "yearly" : "monthly")));
    return ["monthly", "yearly"].filter((x) => set.has(x));
  }, [plans]);

  const activeInterval = intervals.includes(billing) ? billing : intervals[0];
  const visible = intervals.length > 1 ? plans.filter((p) => (p.interval === "yearly" ? "yearly" : "monthly") === activeInterval) : plans;

  return (
    <>
      <PageHero eyebrow={t("mk_pr_eyebrow")} icon={<SellOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />} title={t("m_pricing_title")} subtitle={t("m_pricing_subtitle")} />

      <Section sx={{ pt: { xs: 5, md: 7 } }}>
        {intervals.length > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 4, md: 5 } }}>
            <Tabs value={activeInterval} onChange={(_e, v) => setBilling(v)} aria-label={t("mk_pr_interval_aria")}>
              <Tab value="monthly" label={t("p_monthly")} />
              <Tab value="yearly" label={t("p_yearly")} />
            </Tabs>
          </Box>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress aria-label="Loading" />
          </Box>
        ) : visible.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ color: "text.secondary" }}>{t("mk_pr_empty")}</Typography>
          </Card>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: `repeat(${Math.min(visible.length, 3)}, minmax(0, 1fr))`,
              },
              gap: { xs: 2.5, md: 3 },
              maxWidth: visible.length === 1 ? 480 : visible.length === 2 ? 860 : "none",
              mx: "auto",
              alignItems: "stretch",
            }}
          >
            {visible.map((p) => {
              const featured = Number(p.price) > 100;
              const feats = Object.entries(normalizeFeatures(p.features))
                .filter(([, enabled]) => enabled === true)
                .slice(0, 8)
                .map(([key]) => featureLabel(key));
              const extraSite = Number(p.extra_site_price_monthly || 0);
              return (
                <Card
                  key={p.id}
                  component="article"
                  aria-labelledby={`plan-${p.id}`}
                  sx={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    p: { xs: 3, md: 3.5 },
                    borderColor: featured ? "primary.main" : "divider",
                    borderWidth: featured ? 2 : 1,
                    boxShadow: (th) => (featured ? `0 20px 40px -18px ${alpha(th.palette.primary.main, 0.45)}` : th.aw.shadowSm),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                    <Typography id={`plan-${p.id}`} variant="h2" sx={{ fontSize: "1.25rem" }}>
                      {p.name}
                    </Typography>
                    {featured ? <Pill>{t("mk_pr_popular")}</Pill> : null}
                  </Stack>

                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: { xs: "2.4rem", md: "2.75rem" }, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>
                      ₪{Number(p.price).toFixed(0)}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: "0.95rem" }}>
                      {p.interval === "yearly" ? t("mk_pr_per_year") : t("mk_pr_per_month")}
                    </Typography>
                  </Stack>

                  <Button
                    variant={featured ? "contained" : "outlined"}
                    component={RouterLink}
                    to="/register"
                    fullWidth
                    size="large"
                    sx={{ mt: 2.5, mb: 3 }}
                  >
                    {t("m_pricing_cta")}
                    <Box component="span" sx={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)" }}>
                      {` – ${p.name}`}
                    </Box>
                  </Button>

                  <Divider sx={{ mb: 2.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 1.5 }}>{t("mk_pr_includes")}</Typography>
                  <CheckList
                    dense
                    items={[
                      t("mk_pr_sites", { n: p.limits_sites }),
                      t("mk_pr_pageviews", { n: Number(p.pageviews_included_monthly || 0).toLocaleString(locale) }),
                      ...(Number(p.ai_credits_monthly) > 0 ? [t("mk_pr_ai", { n: Number(p.ai_credits_monthly).toLocaleString(locale) })] : []),
                      ...feats,
                    ]}
                  />
                  {extraSite > 0 ? (
                    <Typography sx={{ color: "text.secondary", fontSize: "0.825rem", mt: 2.5, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}>
                      {t("mk_pr_extra_site", { p: extraSite.toFixed(0) })}
                    </Typography>
                  ) : null}
                </Card>
              );
            })}
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 4, color: "text.secondary" }}>
          <SpeedOutlinedIcon sx={{ fontSize: 18, color: "success.main" }} aria-hidden="true" />
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>{t("mk_pr_note")}</Typography>
        </Stack>

        <Card
          sx={{
            mt: { xs: 5, md: 7 },
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2.5,
            bgcolor: (th) => th.aw.softer,
            boxShadow: "none",
          }}
        >
          <IconTile size={48}>
            <ForumOutlinedIcon />
          </IconTile>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2" sx={{ fontSize: "1.2rem" }}>
              {t("mk_pr_custom_t")}
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{t("mk_pr_custom_d")}</Typography>
          </Box>
          <Button variant="contained" component={RouterLink} to="/contact" endIcon={<DirArrow />}>
            {t("mk_pr_custom_cta")}
          </Button>
        </Card>
      </Section>
    </>
  );
}
